import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom"
import { useMutation, useQuery } from "@tanstack/react-query";
import { TreasuryClient, getActiveClient } from "../lib/client";
import { Page } from "../Components/page/Page";
import { Editable, Enumerable, Labelled } from "../Components";
import { usePersona } from "../Components/Address";
import Title from "../Components/page/Title";
import { ButtonCancel, ButtonOk } from "../Components/buttons";
import { dateFromPython } from "../util";

export default function TreasuryProfile() {
  let navigate = useNavigate();
  let { url } = useParams();
  let persona = usePersona(url);

  let treasury = new TreasuryClient(getActiveClient())
  let { data: profile, isLoading: isLoadingProfile } = useQuery({
    queryKey: ["GET", "/profile", url],
    queryFn: async function getBankingInfoFn() {
      return await treasury.getProfile(url)
    },
  })

  function BankingInfoEditable({ profile = null, ...props }) {

    let { mutate: createProfile } = useMutation({
      mutationFn: async function createSellerProfileFn({ profile }) {
        setIsEditing(false)
        return await treasury.createProfile(url, profile)
      },
      onSuccess: function createProfileSuccessFn(data) {
        navigate(0);
      }
    })

    let { mutate: updateProfile } = useMutation({
      mutationFn: async function updateSellerProfileFn({ profile }) {
        setIsEditing(false)
        return await treasury.updateProfile(url, profile)
      },
      onSuccess: function updateProfileSuccessFn(data) {
        navigate(0);
      }
    });

    function getBankingInfoInput(formData) {
      let bankingInfo = {
        accountHolderName: formData.get("accountHolderName"),
        financialInstitutionName: formData.get("financialInstitutionName"),
        country: formData.get("country"),
        accountType: formData.get("accountType"),
        accountNumber: formData.get("accountNumber"),
      };

      if (bankingInfo.country == "USA") {
        bankingInfo.address = {
          street: formData.get("addressStreet"),
          city: formData.get("addressCity"),
          state: formData.get("addressState"),
          zipCode: formData.get("addressZipCode")
        }
        bankingInfo.routingNumber = formData.get("routingNumber");
      } else {
        bankingInfo.transitNumber = formData.get("transitNumber");
        bankingInfo.institutionNumber = formData.get("institutionNumber");
      }

      return bankingInfo;
    }

    function submitCreateProfile(e) {
      e.preventDefault();
      let bankingInfo = getBankingInfoInput(new FormData(e.target));
      console.log("banking info -- create", bankingInfo)
      createProfile({ profile: { bankingInfo } })
    }

    function submitUpdateProfile(e) {
      e.preventDefault();
      let bankingInfo = getBankingInfoInput(new FormData(e.target));
      console.log("banking info -- update", bankingInfo)
      updateProfile({ profile: { bankingInfo } })
    }

    let [isEditing, setIsEditing] = useState(false);
    let [isCountryUSA, setIsCountryUSA] = useState(profile && profile?.bankingInfo?.country == "USA")

    return <Labelled label="Banking info" className="w-full max-w-1/2">
      <div hidden={!profile && !isEditing}>
        <form onSubmit={!profile ? submitCreateProfile : submitUpdateProfile} className="ml-2 mt-2 max-w-1/3 flex flex-col gap-1" >
          <Labelled label="Account holder name">
            <Editable isEditing={isEditing} name="accountHolderName" required >
              {profile?.bankingInfo?.accountHolderName}
            </Editable>
          </Labelled>
          <Labelled label="Financial institution name">
            <Editable isEditing={isEditing} name="financialInstitutionName" required >
              {profile?.bankingInfo?.financialInstitutionName}
            </Editable>
          </Labelled>
          {isCountryUSA && <Labelled label="Address" >
            <div className="ml-2">
              <Labelled label="Street">
                <Editable isEditing={isEditing} name="addressStreet" >{profile?.bankingInfo?.address?.street}</Editable>
              </Labelled>
              <Labelled label="City">
                <Editable isEditing={isEditing} name="addressCity" >{profile?.bankingInfo?.address?.city}</Editable>
              </Labelled>
              <div className="flex gap-2">
                <Labelled label="State" className="w-full">
                  <Editable isEditing={isEditing} name="addressState" >{profile?.bankingInfo?.address?.state}</Editable>
                </Labelled>
                <Labelled label="ZIP code" className="w-full">
                  <Editable isEditing={isEditing} name="addressZipCode" >{profile?.bankingInfo?.address?.zipCode}</Editable>
                </Labelled>
              </div>
            </div>
          </Labelled>}
          <Labelled label="Country">
            <Enumerable isEditing={isEditing} name="country" options={["Canada", "USA"]} selected="Canada" required
              onChange={(e) => setIsCountryUSA(e.target.value == "USA")} >
              {profile?.bankingInfo?.country}
            </Enumerable>
          </Labelled>
          <Labelled label="Account type">
            <Enumerable isEditing={isEditing} name="accountType" options={["Chequing", "Savings"]} selected="Chequing" required >
              {profile?.bankingInfo?.accountType}
            </Enumerable>
          </Labelled>

          {isCountryUSA &&
            <Labelled label="Routing number">
              <Editable isEditing={isEditing} name="routingNumber" pattern="^[0-9]{9}$" required={isCountryUSA} >
                {profile?.bankingInfo?.routingNumber}
              </Editable>
            </Labelled>}

          {!isCountryUSA && <>
            <Labelled label="Transit number">
              <Editable isEditing={isEditing} name="transitNumber" pattern="^[0-9]{5,6}$" required={!isCountryUSA} >
                {profile?.bankingInfo?.transitNumber}
              </Editable>
            </Labelled>
            <Labelled label="Institution number">
              <Editable isEditing={isEditing} name="institutionNumber" pattern="^[0-9]{3}$" required={!isCountryUSA} >
                {profile?.bankingInfo?.institutionNumber}
              </Editable>
            </Labelled>
          </>}

          <Labelled label="Account number">
            <Editable isEditing={isEditing} name="accountNumber" pattern="^[0-9]+$" required >
              {profile?.bankingInfo?.accountNumber}
            </Editable>
          </Labelled>
          <div className="mt-2">
            {isEditing && < >
              <div className="flex justify-between">
                <ButtonCancel onClick={() => setIsEditing(false)}>Cancel</ButtonCancel>
                <ButtonOk type="submit">{!profile ? "Create" : "Update"}</ButtonOk>
              </div>
              {isCountryUSA ?
                <img className="mt-2 w-full" src="/img/cheque-us.png" /> :
                <img className="mt-2 w-full" src="/img/cheque-ca.png" />
              }
            </>}
          </div>
        </form>
      </div>
      <div className="flex flex-col gap-4 w-fit">
        <span hidden={!!profile || isEditing}>Seller has no banking profile configured!</span>
        <ButtonOk hidden={isEditing} className="w-fit" onClick={() => setIsEditing(true)}>
          {!profile ? "Create" : "Edit"}
        </ButtonOk>
      </div>
    </Labelled>
  }

  return <Page>
    <div className="justify-center flex flex-row">
      <div className="flex flex-col md:w-2/3">
        <Title>Treasury profile</Title>
        <div className="flex flex-col landscape:flex-row gap-4 landscape:gap-8 justify-between">
          <div className="flex flex-col gap-4 w-full">
            <Labelled label="Twin URL"><span className="break-all">{url}</span></Labelled>
            <Labelled label="Persona">
              {persona && <div className="flex flex-col">
                <div>{persona.name}</div>
                <div className="text-xs">{persona.email}</div>
              </div> || "N/A"}
            </Labelled>

            {!!profile && <>
              <Labelled label="Created">{dateFromPython(profile.createdAt)}</Labelled>
              {profile.updatedAt != profile.createdAt && <Labelled label="Updated">
                {dateFromPython(profile.updatedAt)}
              </Labelled>}
              <Labelled label="Seller status">
                {profile.approvedSeller ?
                  <span className="text-success">Approved</span> :
                  <span className="text-warn">Not approved</span>}
              </Labelled>
              <Labelled label="Prior successful deposit">
                {profile.priorSuccessfulDeposit || "N/A"}
              </Labelled>
            </>}

          </div>
          <BankingInfoEditable profile={profile} />
        </div>
      </div>
    </div>
  </Page>
}

